/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Check, Clock, ImagePlus, MapPin, Pencil, Search, Send, Trash2, Users, X,
} from "lucide-react";
import { hostedEventsApi } from "./api";
import { LocationPreview, venueLine } from "../events/location";
import "./HostedEvents.css";

function fmtDate(dstr) {
  return new Date(dstr + "T00:00:00").toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
}

// ── Host: invite picker ──────────────────────────────────────────────────────
// Browse-first: the full opted-in directory (grouped by villa) is fetched once
// on open and cached; search only filters it client-side. Already-invited
// members render checked + disabled and never count towards "Send N invites".
const CHIP_LIMIT = 8;

function VillaGroup({ villa, selected, onToggle, onToggleAll }) {
  const selectable = villa.members.filter((m) => !m.already_invited);
  const picked = selectable.filter((m) => selected[m.user_id]);
  const allPicked = selectable.length > 0 && picked.length === selectable.length;
  const somePicked = picked.length > 0 && !allPicked;

  return (
    <div className="he-villa-group">
      <div className="he-villa-head">
        <span className="he-villa-name">{villa.villa}{villa.mine ? " · my villa" : ""}</span>
        <label className="he-villa-all">
          <input
            type="checkbox"
            checked={allPicked}
            disabled={selectable.length === 0}
            ref={(el) => { if (el) el.indeterminate = somePicked; }}
            onChange={() => onToggleAll(selectable.map((m) => m.user_id), !allPicked)}
          />
          all
        </label>
      </div>
      {villa.members.map((m) => (
        <label key={m.user_id} className={`he-member ${m.already_invited ? "he-member-done" : ""}`}>
          <input
            type="checkbox"
            checked={m.already_invited || !!selected[m.user_id]}
            disabled={m.already_invited}
            onChange={() => onToggle(m.user_id)}
          />
          <span className="he-member-name">{m.name}</span>
          {m.already_invited && <span className="he-member-villa">already invited</span>}
        </label>
      ))}
    </div>
  );
}

function InvitePicker({ eventId, onInvited }) {
  const [villas, setVillas] = useState(null);       // null = loading
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});     // user_id -> true
  const [prevIds, setPrevIds] = useState(undefined); // undefined = not fetched, null = known-empty
  const [prevBusy, setPrevBusy] = useState(false);
  const [chipsExpanded, setChipsExpanded] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const loadDirectory = useCallback(async () => {
    try {
      const r = await hostedEventsApi.invitable(eventId);
      if (aliveRef.current) setVillas(r.villas || []);
    } catch (e) {
      if (aliveRef.current) {
        setError(e.message || "Could not load members");
        setVillas((v) => v || []);
      }
    }
  }, [eventId]);

  useEffect(() => {
    setVillas(null);
    setSelected({});
    loadDirectory();
  }, [loadDirectory]);

  const memberById = useMemo(() => {
    const map = {};
    for (const v of villas || [])
      for (const m of v.members) map[m.user_id] = { ...m, villa: v.villa };
    return map;
  }, [villas]);

  const selectableIds = useMemo(
    () => Object.keys(memberById).filter((uid) => !memberById[uid].already_invited),
    [memberById]
  );

  // Live filter — matches member name OR villa label, no minimum length.
  const visible = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return villas || [];
    return (villas || [])
      .map((v) =>
        v.villa.toLowerCase().includes(ql)
          ? v
          : { ...v, members: v.members.filter((m) => m.name.toLowerCase().includes(ql)) }
      )
      .filter((v) => v.members.length > 0);
  }, [villas, q]);

  const toggle = (uid) => {
    if (memberById[uid]?.already_invited) return;
    setSelected((s) => ({ ...s, [uid]: !s[uid] }));
  };

  const addMany = (ids) =>
    setSelected((s) => {
      const next = { ...s };
      for (const uid of ids) if (memberById[uid] && !memberById[uid].already_invited) next[uid] = true;
      return next;
    });

  const toggleAll = (ids, on) =>
    setSelected((s) => {
      const next = { ...s };
      for (const uid of ids) {
        if (on) next[uid] = true;
        else delete next[uid];
      }
      return next;
    });

  // Quick groups (additive selections)
  const pickEveryone = () => {
    if (!window.confirm(`Invite all ${selectableIds.length} members?`)) return;
    addMany(selectableIds);
  };

  const pickPrevious = async () => {
    if (Array.isArray(prevIds)) { addMany(prevIds); return; }
    setPrevBusy(true);
    setError("");
    try {
      const r = await hostedEventsApi.previousGuests(eventId);
      const ids = (r.user_ids || []).filter(
        (uid) => memberById[uid] && !memberById[uid].already_invited
      );
      if (ids.length === 0) {
        setPrevIds(null); // nothing to add — hide the button
      } else {
        setPrevIds(ids);
        addMany(ids);
      }
    } catch (e) {
      setError(e.message || "Could not load previous guests");
    } finally {
      setPrevBusy(false);
    }
  };

  const pickMyVilla = () => {
    const ids = (villas || [])
      .filter((v) => v.mine)
      .flatMap((v) => v.members.filter((m) => !m.already_invited).map((m) => m.user_id));
    addMany(ids);
  };

  const send = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return;
    setSending(true);
    setError("");
    try {
      await hostedEventsApi.invite(eventId, ids);
      setSelected({});
      setChipsExpanded(false);
      // Refresh the cached directory so the new invitees flip to
      // checked+disabled (already_invited) instead of staying selectable.
      loadDirectory();
      onInvited();
    } catch (e) {
      setError(e.message || "Could not send invites");
    } finally {
      setSending(false);
    }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const count = selectedIds.length;
  const chipIds = chipsExpanded ? selectedIds : selectedIds.slice(0, CHIP_LIMIT);
  const hiddenChips = selectedIds.length - chipIds.length;
  const hasMyVilla = (villas || []).some((v) => v.mine);

  return (
    <div className="he-invite">
      <div className="he-search">
        <Search size={15} />
        <input placeholder="Search name or villa…" value={q}
          onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="he-quick-groups">
        <button type="button" className="he-quick-btn" onClick={pickEveryone}
          disabled={!villas || selectableIds.length === 0}>
          Everyone
        </button>
        {prevIds !== null && (
          <button type="button" className="he-quick-btn" onClick={pickPrevious}
            disabled={!villas || prevBusy}>
            {prevBusy ? "Loading…" : "Previously invited"}
          </button>
        )}
        {hasMyVilla && (
          <button type="button" className="he-quick-btn" onClick={pickMyVilla}>
            My villa
          </button>
        )}
      </div>

      {count > 0 && (
        <div className="he-chips">
          {chipIds.map((uid) => (
            <span key={uid} className="he-chip">
              {memberById[uid]?.name || "Member"}
              <button type="button" className="he-chip-x" aria-label={`Remove ${memberById[uid]?.name || "member"}`}
                onClick={() => toggle(uid)}>
                <X size={12} />
              </button>
            </span>
          ))}
          {hiddenChips > 0 && (
            <button type="button" className="he-chip he-chip-more"
              onClick={() => setChipsExpanded(true)}>
              +{hiddenChips} more
            </button>
          )}
          <button type="button" className="he-clear-all" onClick={() => { setSelected({}); setChipsExpanded(false); }}>
            Clear all
          </button>
        </div>
      )}

      {error && <p className="he-error">{error}</p>}

      {villas === null ? (
        <div className="he-member-list he-dir-skeleton">
          {[...Array(5)].map((_, i) => <div key={i} className="he-skel-row" />)}
        </div>
      ) : visible.length === 0 ? (
        <p className="he-muted">
          {q.trim() ? "No members match your search." : "No invitable members found."}
        </p>
      ) : (
        <div className="he-member-list he-dir-list">
          {visible.map((v) => (
            <VillaGroup key={v.villa} villa={v} selected={selected}
              onToggle={toggle} onToggleAll={toggleAll} />
          ))}
        </div>
      )}

      <div className="he-invite-footer">
        <button className="he-primary" disabled={sending || count === 0} onClick={send}>
          <Send size={15} /> {sending ? "Sending…" : `Send ${count || ""} invite${count === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}

// ── Host: invitation card ────────────────────────────────────────────────────
function CardManager({ ev, cardUrl, onUploaded }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const upload = async (file) => {
    setBusy(true);
    setError("");
    try {
      await hostedEventsApi.uploadCard(ev.id, file);
      onUploaded();
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="he-card-mgr">
      {cardUrl && <img className="he-card-img" src={cardUrl} alt="Invitation card" />}
      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
      <button className="he-secondary" disabled={busy}
        onClick={() => fileRef.current?.click()}>
        <ImagePlus size={15} /> {busy ? "Uploading…" : ev.has_card ? "Replace card" : "Add invitation card"}
      </button>
      {error && <p className="he-error">{error}</p>}
    </div>
  );
}

// ── Host: guest list ─────────────────────────────────────────────────────────
function GuestList({ ev }) {
  const rsvpLabel = { yes: "Going", no: "Declined", pending: "Pending" };
  const rsvpCls = { yes: "he-g-yes", no: "he-g-no", pending: "he-g-pending" };
  return (
    <div className="he-guests">
      <div className="he-tally">
        <span className="he-c-yes">{ev.counts.yes} going</span>
        <span className="he-c-no">{ev.counts.no} declined</span>
        <span className="he-c-pending">{ev.counts.pending} pending</span>
        {ev.rsvp_locked && <span className="he-pill he-pill-final">Finalized</span>}
      </div>
      {(ev.guests || []).length === 0 ? (
        <p className="he-muted">No one invited yet.</p>
      ) : (
        <ul className="he-guest-rows">
          {ev.guests.map((g) => (
            <li key={g.user_id} className="he-guest-row">
              <span>{g.name}</span>
              <span className={`he-g-badge ${rsvpCls[g.rsvp]}`}>{rsvpLabel[g.rsvp]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Invitee: RSVP ────────────────────────────────────────────────────────────
function RsvpBox({ ev, onRsvp }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const respond = async (response) => {
    setBusy(true);
    setError("");
    try {
      await onRsvp(response);
    } catch (e) {
      setError(e.message || "Could not RSVP");
    } finally {
      setBusy(false);
    }
  };
  if (ev.rsvp_locked) {
    return (
      <div className="he-rsvp">
        <p className="he-muted">
          RSVP is closed — the guest list has been finalized.
          {ev.my_rsvp && ev.my_rsvp !== "pending" &&
            ` You replied: ${ev.my_rsvp === "yes" ? "Going" : "Not going"}.`}
        </p>
      </div>
    );
  }
  return (
    <div className="he-rsvp">
      <p className="he-rsvp-q">Will you attend?</p>
      <div className="he-rsvp-btns">
        <button className={`he-rsvp-yes ${ev.my_rsvp === "yes" ? "on" : ""}`}
          disabled={busy} onClick={() => respond("yes")}>
          <Check size={16} /> Going
        </button>
        <button className={`he-rsvp-no ${ev.my_rsvp === "no" ? "on" : ""}`}
          disabled={busy} onClick={() => respond("no")}>
          <X size={16} /> Can't make it
        </button>
      </div>
      <p className="he-tally">
        <span className="he-c-yes">{ev.counts.yes} going</span>
        <span className="he-c-pending">{ev.counts.pending} yet to reply</span>
      </p>
    </div>
  );
}

export default function HostedEventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [cardUrl, setCardUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await hostedEventsApi.get(id);
      setEv(data);
      if (data.has_card) {
        const url = await hostedEventsApi.cardObjectUrl(id);
        setCardUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      }
    } catch (e) {
      setError(e.message || "Could not load event");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (cardUrl) URL.revokeObjectURL(cardUrl); }, [cardUrl]);

  const onRsvp = async (response) => {
    await hostedEventsApi.rsvp(id, response);
    await load();
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this event? Invitees will no longer see it.")) return;
    try {
      await hostedEventsApi.cancel(id);
      navigate("/events?tab=hosted");
    } catch (e) {
      alert(e.message || "Could not cancel");
    }
  };

  if (loading) return <div className="he-loading">Loading…</div>;
  if (error) return <div className="he-page"><p className="he-error">{error}</p></div>;
  if (!ev) return null;

  return (
    <div className="he-page he-detail">
      <div className="he-back-bar">
        <button className="he-back" onClick={() => navigate("/events?tab=hosted")}>
          <ArrowLeft size={18} /> Events
        </button>
      </div>

      <div className="he-detail-head">
        <h1 className="he-heading">{ev.title}</h1>
        {ev.is_host && ev.status !== "cancelled" && (
          <div className="he-detail-actions">
            <button className="he-secondary" onClick={() => navigate(`/hosted-events/${id}/edit`)}>
              <Pencil size={14} /> Edit
            </button>
            <button className="he-danger" onClick={cancel}>
              <Trash2 size={14} /> Cancel
            </button>
          </div>
        )}
      </div>

      <div className="he-meta-block">
        <div className="he-meta"><Clock size={14} /> <span>{fmtDate(ev.event_date)}{ev.start_time ? ` · ${ev.start_time}` : ""}{ev.end_time ? `–${ev.end_time}` : ""}</span></div>
        {venueLine(ev.location, ev.venue) && (
          <div className="he-meta"><MapPin size={14} /> <span>{venueLine(ev.location, ev.venue)}</span></div>
        )}
        {ev.host && !ev.is_host && <div className="he-meta he-muted">Hosted by {ev.host.name}</div>}
      </div>

      <LocationPreview location={ev.location} />

      {ev.description && <p className="he-desc">{ev.description}</p>}

      {/* Invitee view: card + RSVP (counts only) */}
      {!ev.is_host && cardUrl && (
        <img className="he-card-img" src={cardUrl} alt="Invitation card" />
      )}
      {ev.am_invited && !ev.is_host && <RsvpBox ev={ev} onRsvp={onRsvp} />}

      {/* Host view: card + invite + guest list */}
      {ev.is_host && (
        <>
          <section className="he-section">
            <h2 className="he-sec-title">Invitation card</h2>
            <CardManager ev={ev} cardUrl={cardUrl} onUploaded={load} />
          </section>

          {ev.status !== "cancelled" && !ev.rsvp_locked && (
            <section className="he-section">
              <h2 className="he-sec-title">Invite members</h2>
              <p className="he-muted he-sec-hint">
                Members who opted out of event invites won't appear here.
                Invitations can be sent up to 3 days before the event.
              </p>
              <InvitePicker eventId={id} onInvited={load} />
            </section>
          )}

          <section className="he-section">
            <h2 className="he-sec-title"><Users size={16} /> Guest list</h2>
            <GuestList ev={ev} />
          </section>
        </>
      )}
    </div>
  );
}
